import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-json';
import { ACE_THEME } from '../../../lib/aceTheme';
import 'ace-builds/src-noconflict/ext-language_tools';

import Navbar from '../../../components/Navbar';
import PageWithAsyncData from '../../../components/PageWithAsyncData';
import { Input } from '../../../components/elements/Input';
import { PSmall, LabelUppercase } from '../../../components/elements/Text';
import { PageTitle } from '../../../components/Admin/AdminLayout';
import { z } from 'zod';
import {
  AssessmentTestStepSchema,
  AssessmentTestDimensionSchema,
} from '../../../schemas/assessment-test';
import type {
  AssessmentTestStep,
  AssessmentTestDimension,
} from '../../../schemas/assessment-test';
import { ROUTES } from '../../../routing';
import {
  useCmsGetAssessmentTest,
  useCmsCreateAssessmentTest,
  useCmsUpdateAssessmentTest,
} from '../../../hooks/use-api/admin';
import CMSPageWrapper, {
  BottomBar,
  SmallButton,
} from '../../../components/Admin/CMSPageWrapper';
import { CMS_SIDEBAR_PAGES } from '../../../components/Admin/CMSSideBar';

// Steps and dimensions are authored as one object so there is a single box to
// paste into and keep in sync, matching the request body the API expects.
const DefinitionSchema = z.object({
  name: z.string().optional(),
  slug: z.string().optional(),
  steps: z.array(AssessmentTestStepSchema),
  dimensions: z.array(AssessmentTestDimensionSchema),
});

type AssessmentTestDefinitionInput = {
  name?: string;
  slug?: string;
  steps: AssessmentTestStep[];
  dimensions: AssessmentTestDimension[];
};

const EMPTY_DEFINITION = JSON.stringify(
  { name: '', slug: '', steps: [], dimensions: [] },
  null,
  2
);

function formatZodError(error: z.ZodError): string {
  return error.issues
    .slice(0, 3)
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
      return `${path}: ${issue.message}`;
    })
    .join('\n');
}

type ValidationResult<T> =
  | { valid: true; data: T; error: null }
  | { valid: false; data: null; error: string };

function validateDefinition(
  json: string
): ValidationResult<AssessmentTestDefinitionInput> {
  try {
    const parsed = JSON.parse(json);
    const result = DefinitionSchema.safeParse(parsed);
    if (!result.success) {
      return { valid: false, data: null, error: formatZodError(result.error) };
    }
    return { valid: true, data: result.data, error: null };
  } catch (e) {
    return {
      valid: false,
      data: null,
      error: `Invalid JSON: ${(e as Error).message}`,
    };
  }
}

const PREVIEW_STORAGE_KEY = 'enki:preview:assessment-test';

const EditorPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-shrink: 0;

  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;

const FormGroup = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  flex: ${({ $flex }) => $flex ?? 1};
  min-height: 0;
`;

// enki addition (not in the source editor): pasting definitions also parks a
// copy in localStorage for the preview tab, so the editor needs a way to drop
// both at once rather than leaving a stale payload behind.
const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 8px;
  font-family: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textDanger};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Label = styled(LabelUppercase)`
  margin-bottom: 8px;
  flex-shrink: 0;
  margin-top: 0;
`;

const EditorWrapper = styled.div<{ hasError?: boolean }>`
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.textDanger : theme.colors.bgActive};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgDefault};
  transition: border-color 200ms ease-in-out;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ErrorText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
  margin: 8px 0 0;
  white-space: pre-line;
`;

export default function AssessmentTestEditor() {
  const { slug } = useParams<{ slug: string }>();
  const isEditMode = !!slug;
  const history = useHistory();

  const {
    data: existingTest,
    isLoading,
    error,
    refetch,
  } = useCmsGetAssessmentTest(slug ?? '');
  const createMutation = useCmsCreateAssessmentTest();
  const updateMutation = useCmsUpdateAssessmentTest(slug ?? '');

  const [name, setName] = React.useState('');
  const [testSlug, setTestSlug] = React.useState('');
  const [definitionJson, setDefinitionJson] =
    React.useState(EMPTY_DEFINITION);
  const [definitionError, setDefinitionError] =
    React.useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = React.useState(false);

  const existingDefinitionJson = React.useMemo(
    () =>
      existingTest
        ? JSON.stringify(
            {
              name: existingTest.name,
              slug: existingTest.slug,
              steps: existingTest.steps,
              dimensions: existingTest.dimensions,
            },
            null,
            2
          )
        : null,
    [existingTest]
  );

  React.useEffect(() => {
    if (existingTest && existingDefinitionJson && !hasInitialized) {
      setName(existingTest.name);
      setTestSlug(existingTest.slug);
      setDefinitionJson(existingDefinitionJson);
      setHasInitialized(true);
    }
  }, [existingTest, existingDefinitionJson, hasInitialized]);

  const handleNameChange = (value: string) => {
    setName(value);
  };

  const handleSlugChange = (value: string) => {
    setTestSlug(value);
  };

  const handleDefinitionChange = (value: string) => {
    setDefinitionJson(value);
    const result = validateDefinition(value);
    setDefinitionError(result.error);

    if (result.valid) {
      if (result.data.name) setName(result.data.name);
      if (result.data.slug) setTestSlug(result.data.slug);
    }
  };

  const definitionResult = validateDefinition(definitionJson);
  const isJsonValid = definitionResult.valid;
  const hasContent = definitionResult.valid && definitionResult.data.steps.length > 0;

  const handleSave = async () => {
    if (!name.trim() || !testSlug.trim()) {
      toast.error('Name and slug are required');
      return;
    }

    if (!definitionResult.valid) return;

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({
          name,
          steps: definitionResult.data.steps,
          dimensions: definitionResult.data.dimensions,
        });
        toast.success('Assessment test updated');
      } else {
        await createMutation.mutateAsync({
          name,
          slug: testSlug,
          steps: definitionResult.data.steps,
          dimensions: definitionResult.data.dimensions,
        });
        toast.success('Assessment test created');
        history.push(ROUTES.ADMIN.CMS_ASSESSMENT_TESTS.getPath());
      }
    } catch (e) {
      const message = (e as Error).message || 'Something went wrong';
      toast.error(message);
    }
  };

  const handlePreview = () => {
    if (!definitionResult.valid) return;

    const previewData = {
      name,
      slug: testSlug,
      steps: definitionResult.data.steps,
      dimensions: definitionResult.data.dimensions,
    };

    localStorage.setItem(
      PREVIEW_STORAGE_KEY,
      JSON.stringify({ ...previewData, _createdAt: Date.now() })
    );
    window.open(
      ROUTES.ADMIN.CMS_ASSESSMENT_TEST_PREVIEW.getPath({
        slug: testSlug.trim(),
      }),
      '_blank'
    );
  };

  // Clears the pasted definition and the preview copy parked in localStorage,
  // so a stale payload can't linger behind a later preview.
  const handleClear = () => {
    setDefinitionJson(EMPTY_DEFINITION);
    setDefinitionError(null);
    localStorage.removeItem(PREVIEW_STORAGE_KEY);
    toast.success('Cleared the pasted definition and the saved preview');
  };

  const isDefinitionEmpty = ['', '{}', EMPTY_DEFINITION].includes(
    definitionJson.trim()
  );

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const hasChanges =
    isEditMode && existingTest
      ? name !== existingTest.name ||
        definitionJson !== existingDefinitionJson
      : true;

  const editorProps = {
    isEditMode,
    name,
    testSlug,
    definitionJson,
    definitionError,
    isSaving,
    isJsonValid,
    hasContent,
    hasChanges,
    onNameChange: handleNameChange,
    onSlugChange: handleSlugChange,
    onDefinitionJsonChange: handleDefinitionChange,
    onSave: handleSave,
    onPreview: handlePreview,
    onClear: handleClear,
    isDefinitionEmpty,
  };

  if (isEditMode) {
    return (
      <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
        <EditorContent {...editorProps} />
      </PageWithAsyncData>
    );
  }

  return <EditorContent {...editorProps} />;
}

function EditorContent({
  isEditMode,
  name,
  testSlug,
  definitionJson,
  definitionError,
  isSaving,
  isJsonValid,
  hasContent,
  hasChanges,
  onNameChange,
  onSlugChange,
  onDefinitionJsonChange,
  onSave,
  onPreview,
  onClear,
  isDefinitionEmpty,
}: {
  isEditMode: boolean;
  name: string;
  testSlug: string;
  definitionJson: string;
  definitionError: string | null;
  isSaving: boolean;
  isJsonValid: boolean;
  hasContent: boolean;
  hasChanges: boolean;
  onNameChange: (value: string) => void;
  onSlugChange: (value: string) => void;
  onDefinitionJsonChange: (value: string) => void;
  onSave: () => void;
  onPreview: () => void;
  onClear: () => void;
  isDefinitionEmpty: boolean;
}) {
  return (
    <>
      <Navbar
        breadCrumbs={[
          { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
          {
            label: 'Assessment Tests',
            url: ROUTES.ADMIN.CMS_ASSESSMENT_TESTS.getPath(),
          },
          { label: isEditMode ? 'Edit' : 'Create', url: null },
        ]}
      />
      <CMSPageWrapper
        currentPage={CMS_SIDEBAR_PAGES.ASSESSMENT_TESTS}
        shouldAddEmptyDiv={false}
        bottomBar={
          <BottomBar>
            <SmallButton
              label="Preview"
              type="secondary"
              onClick={onPreview}
              faIcon="fa-solid fa-eye"
              showIconBeforeLabel
              isDisabled={!isJsonValid || !hasContent}
            />
            <SmallButton
              label={isEditMode ? 'Save Changes' : 'Create Test'}
              onClick={onSave}
              type="primary"
              isLoading={isSaving}
              showIconBeforeLabel
              faIcon={isEditMode ? 'fa-solid fa-save' : 'fa-solid fa-plus'}
              isDisabled={!isJsonValid || !hasChanges}
            />
          </BottomBar>
        }
      >
        <PageTitle>
          {isEditMode ? 'Edit Assessment Test' : 'Create Assessment Test'}
        </PageTitle>
        <EditorPageLayout>
          <FieldRow>
            <FormGroup>
              <Label>Name</Label>
              <Input
                placeholder="e.g. AI Readiness Assessment"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                style={{ marginTop: 0, paddingBottom: 0 }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Slug</Label>
              <Input
                placeholder="e.g. ai-readiness"
                value={testSlug}
                onChange={(e) => onSlugChange(e.target.value)}
                isDisabled={isEditMode}
                style={{ marginTop: 0, paddingBottom: 0 }}
              />
            </FormGroup>
          </FieldRow>

          <FormGroup $flex={3}>
            <LabelRow>
              <Label>Definition (JSON)</Label>
              <ClearButton
                type="button"
                onClick={onClear}
                disabled={isDefinitionEmpty}
              >
                Clear
              </ClearButton>
            </LabelRow>
            <EditorWrapper hasError={!!definitionError}>
              <AceEditor
                mode="json"
                theme={ACE_THEME}
                name="definition-editor"
                value={definitionJson}
                onChange={onDefinitionJsonChange}
                width="100%"
                height="100%"
                fontSize={14}
                showPrintMargin={false}
                showGutter
                highlightActiveLine
                wrapEnabled
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                  tabSize: 2,
                  wrap: true,
                }}
                style={{ background: 'transparent', flex: 1 }}
              />
            </EditorWrapper>
            {definitionError && <ErrorText>{definitionError}</ErrorText>}
          </FormGroup>
        </EditorPageLayout>
      </CMSPageWrapper>
    </>
  );
}
